import { themeVars } from '@synoptic/ui-kit/theme/vars.ts';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@synoptic/ui-kit/tooltip/tooltip.tsx';
import { clsx } from 'clsx';
import { ComponentPropsWithoutRef, FC, ReactNode } from 'react';
import {
  statusItem,
  statusItemIcon,
  statusItemTooltipContent,
  statusItemContainer,
} from '@/components/interactive-status/interactive-status.css.ts';

interface InteractiveStatusProps {
  icon: ReactNode;
  variant?: 'active' | 'inactive' | 'error';
  tooltipTitle?: string;
  tooltipDescription?: string;
  tooltipAction?: ReactNode;
  containerClassName?: string;
  iconClassName?: string;
  iconButtonAfter?: ReactNode;
}

const getIconColor = (
  variant: NonNullable<InteractiveStatusProps['variant']>,
  hasText: boolean,
) => {
  switch (variant) {
    case 'active':
      return themeVars.container.foreground.brand;
    case 'inactive': {
      // TODO: don't really like it, not obvious, refactor better maybe or use different component.
      // For icon only cases (sound) disabled color looks like the button is not clickable, primary feels better

      return hasText
        ? themeVars.color.icon.disabled
        : themeVars.container.foreground.secondary;
    }
    case 'error': {
      return themeVars.color.icon.alert.critical;
    }
    default: {
      return themeVars.color.icon.primary;
    }
  }
};

export const InteractiveStatus: FC<
  ComponentPropsWithoutRef<'div'> & InteractiveStatusProps
> = ({
  icon,
  variant = 'active',
  tooltipTitle,
  tooltipDescription,
  tooltipAction,
  children,
  className,
  containerClassName,
  iconClassName,
  iconButtonAfter,
  ...props
}) => {
  const color = getIconColor(variant, Boolean(children));

  return (
    <div className={clsx(statusItemContainer, containerClassName)}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div
            data-clickable={props.onClick ? '' : undefined}
            className={clsx(statusItem, className)}
            {...props}
          >
            <div
              style={{ color }}
              className={clsx(statusItemIcon, iconClassName)}
            >
              {icon}
            </div>
            {children}
          </div>
        </TooltipTrigger>
        <TooltipContent
          className={statusItemTooltipContent}
          side={'bottom'}
          title={tooltipTitle}
          description={tooltipDescription}
          action={tooltipAction}
        />
      </Tooltip>

      {iconButtonAfter}
    </div>
  );
};
